/**
 * An operation type as a designer source.
 *
 * The server serves a type in the designer's own envelope — one operation, its
 * sub-operations as steps — and takes the same verbs. A step is addressed by its code on the
 * page and by its link id on the wire; the source keeps the last envelope to translate.
 * A type has no roles (the library cannot know who performs a step in a given plant), no
 * rules to bind from here, no processes, no concurrency: those verbs are simply absent, and
 * the designer draws nothing for them.
 */
import Axios from "axios";
import React, { useEffect, useState } from "react";
import { Button, Modal, Input, message as antdMessage } from "antd";

import { getVocabulary, listRoles, newKey, write } from "@/pages/Batch-Processing/mbmr/designerApi";
import { StepTypePicker, useVocabulary } from "@/designer/components";

const BASE = "/bp/type-library";
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("Token")}` });

/** The step types this type allows, editable in place. What is chosen here is what the step
 *  picker offers — on this page, and on every record operation built from the type. */
function AllowedStepTypes({ typeId, state, helpers }) {
  const vocab = useVocabulary(getVocabulary);
  const saved = state?.type?.allowed_step_types || [];
  const [draft, setDraft] = useState(saved);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDraft(state?.type?.allowed_step_types || []); }, [state?.type?.allowed_step_types]);
  const dirty = draft.length !== saved.length || draft.some((v) => !saved.includes(v));
  const save = async () => {
    setSaving(true);
    try {
      const res = await write("post", `${BASE}/${typeId}/step-types/`, { step_types: draft },
        { version: state?.version, key: newKey() });
      if (res.ok) { antdMessage.open({ type: "success", content: "Step types saved." }); await helpers.reload?.(); }
      else antdMessage.open({ type: res.conflict ? "warning" : "error", content: res.message || "The step types could not be saved." });
    } finally { setSaving(false); }
  };
  return (
    <>
      <span className="dz-eyebrow" style={{ marginTop: 6 }}>Step types this operation uses</span>
      <StepTypePicker vocab={vocab} value={draft} onChange={setDraft} disabled={!!helpers.readOnly || saving} />
      {!helpers.readOnly && dirty ? (
        <div style={{ display: "flex", gap: 8 }}>
          <Button size="small" type="primary" loading={saving} onClick={save}>Save step types</Button>
          <Button size="small" disabled={saving} onClick={() => setDraft(saved)}>Cancel</Button>
        </div>
      ) : null}
    </>
  );
}

export function typeSource(typeId, { edit = false } = {}) {
  let last = null;
  const steps = () => last?.authored?.recipe?.operations?.[0]?.children || [];
  // A flat type's step is addressed by its link id; a template's by its code (there is no
  // link row). Steps inside a loop or a lane are found by walking the tree.
  const all = (nodes) => nodes.flatMap((c) => [c, ...all(c.children || [])]);
  const linkOf = (code) => all(steps()).find((c) => c.code === code)?.link_id ?? code;
  const remember = (res) => { if (res?.authored) last = res; return res; };

  return {
    kind: "type",
    id: typeId,
    backTo: "/operation-type",
    backLabel: "Back to the library",
    showRole: (state) => !!state?.template_from,
    // Master data opens in View; Edit is the deliberate step (every write bumps the version).
    // …unless the page was opened to edit (`?edit=1`).
    startReadOnly: !edit,
    newKey,
    getVocabulary,
    // The role master, so a template's default role (a name, resolved to the plant's id) has
    // options to show against and a pick to write back (F7).
    listRoles,
    load: async () => {
      const res = await Axios.get(`${BASE}/${typeId}/designer/`, { headers: auth() });
      return remember(res.data);
    },
    insertStep: async (body, ctx) =>
      remember(await write("post", `${BASE}/${typeId}/steps/`, body, ctx)),
    editStep: async (code, body, ctx) =>
      remember(await write("patch", `${BASE}/${typeId}/steps/${linkOf(code)}/`, body, ctx)),
    moveStep: async (code, body, ctx) =>
      remember(await write("post", `${BASE}/${typeId}/steps/${linkOf(code)}/move/`, body, ctx)),
    deleteStep: async (code, body, ctx) =>
      remember(await write("post", `${BASE}/${typeId}/steps/${linkOf(code)}/delete/`, body, ctx)),
    // F2: the record's structure verbs, on the template. The type is the one "operation",
    // so the operation id the designer passes is ignored on the wire.
    wrapInLoop: async (_opId, body, ctx) =>
      remember(await write("post", `${BASE}/${typeId}/loops/`, body, ctx)),
    insertDecision: async (body, ctx) =>
      remember(await write("post", `${BASE}/${typeId}/decisions/`, body, ctx)),
    setConcurrency: async (_opId, body, ctx) =>
      remember(await write("post", `${BASE}/${typeId}/concurrency/`, body, ctx)),
    setHandover: async (_opId, body, ctx) =>
      remember(await write("post", `${BASE}/${typeId}/handover/`, body, ctx)),
    // A sub-operation carries its library rules with it; nothing else hangs off it.
    getImpact: async (code) => {
      const s = steps().find((c) => c.code === code);
      return { impact: { steps: 1, bindings: (s?.rules || []).length, codes: [code], payload_rows: 0 } };
    },
    /** The blast radius: what a change here reaches. Shown above the step form. */
    blastRadius: (state) => {
      const n = state?.recipe_count || 0;
      // What is true: a record copies at authoring time and keeps its steps. A change here
      // becomes the next version, which each record may update to from its own page.
      return n ? `${n} record${n === 1 ? " was" : "s were"} built from this type and keep${n === 1 ? "s" : ""} its own steps. A change here becomes the next version; each record can update to it from its operation.` : null;
    },
    archive: async (reason) => {
      const res = await Axios.post(`${BASE}/${typeId}/archive/`, { reason }, { headers: auth() });
      return res.data;
    },
    renderDetails: (state, helpers = {}) => {
      const t = state?.type || {};
      const byVersion = (t.by_version || []).map((r) => `${r.records} on ${r.version == null ? "an unstamped version" : `v${r.version}`}`).join(", ");
      const askArchive = () => {
        let reason = "";
        Modal.confirm({
          title: `Archive ${t.name}?`,
          content: (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
              <span>Never a delete: the type, its versions and its history stay, and it can be restored. A reason is required and is recorded on the version.</span>
              <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} placeholder="Why this type is being archived" onChange={(e) => { reason = e.target.value; }} />
            </div>
          ),
          okText: "Archive",
          okButtonProps: { danger: true },
          onOk: async () => {
            try {
              await Axios.post(`${BASE}/${typeId}/archive/`, { reason }, { headers: auth() });
              antdMessage.open({ type: "success", content: `${t.name} archived.` });
              helpers.navigate?.("/operation-type");
            } catch (err) {
              antdMessage.open({ type: "error", content: err?.response?.data?.message || "The type could not be archived." });
              throw err;
            }
          },
        });
      };
      const rows = [
        ["Code", t.code || "— none"], ["Phase", t.phase || "— not stated"], ["Category", t.category || "—"],
        ["Applies to", (t.classes || []).join(", ") || "any"], ["Equipment", t.equipment || "—"],
        ["SOP", t.sop || "—"], ["Typical duration", t.duration_hours ? `${t.duration_hours} h` : "—"],
        ["E-signature", t.esign_required ? "required" : "not required"],
        ["Supervisor approval", t.supervisor_approval ? "required" : "not required"],
        ["Version", `${t.version ?? state?.version ?? 0}${t.version_reason ? ` · ${t.version_reason}` : ""}`],
        ["Used by", `${state?.recipe_count || 0} record${state?.recipe_count === 1 ? "" : "s"}${byVersion ? ` · ${byVersion}` : ""}`],
      ];
      if (t.specialises) rows.splice(3, 0, ["Variant of", <a onClick={() => helpers.navigate?.(`/operation-type/${t.specialises.id}`)}>{t.specialises.name}</a>]);
      if (t.variants?.length) rows.splice(t.specialises ? 4 : 3, 0, ["Variants", (
        <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
          {t.variants.map((v) => <a key={v.id} onClick={() => helpers.navigate?.(`/operation-type/${v.id}`)}>{v.name}</a>)}
        </span>
      )]);
      const from = state?.template_from;
      const lifted = !!from?.lifted;
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span className="dz-eyebrow">The type</span>
          {rows.map(([k, v]) => (
            <div key={k} className="dz-kv"><span className="k">{k}</span><span className="v">{v}</span></div>
          ))}
          {t.description ? <span style={{ fontSize: 12.5 }}>{t.description}</span> : null}
          <AllowedStepTypes typeId={typeId} state={state} helpers={helpers} />
          {t.versions?.length ? (
            <>
              <span className="dz-eyebrow" style={{ marginTop: 6 }}>Versions — each one a state the library keeps</span>
              {t.versions.map((v) => (
                <div key={v.version} className="dz-kv" style={{ alignItems: "flex-start" }}>
                  <span className="k dz-code">v{v.version}</span>
                  <span className="v" style={{ fontSize: 12 }}>
                    {v.reason || "—"}
                    <span style={{ display: "block", opacity: .7 }}>{new Date(v.changed_on).toLocaleString()}{v.by ? ` · ${v.by}` : ""}</span>
                  </span>
                </div>
              ))}
            </>
          ) : null}
          {!state?.recipe_count && !t.archived ? (
            <>
              <span className="dz-eyebrow" style={{ marginTop: 6 }}>Archive</span>
              <div>
                <Button size="small" danger disabled={helpers.busy} onClick={askArchive}>Archive this type</Button>
              </div>
              <span style={{ fontSize: 11.5, opacity: .75 }}>No record is built from it. Archiving keeps the row, its versions and its history; it is never deleted.</span>
            </>
          ) : null}
          {from && !lifted ? (
            <>
              <span className="dz-eyebrow" style={{ marginTop: 6 }}>Template</span>
              <span style={{ fontSize: 12.5 }}>
                Distilled from record <b>{from.recipe_code}</b> · {from.operation_name}
                {from.promoted_on ? ` · ${new Date(from.promoted_on).toLocaleDateString()}` : ""}.
                Its repeats, decisions, stations and handover are authored here and replayed onto every
                record built from it. Roles are defaults the record may change.
              </span>
            </>
          ) : null}
        </div>
      );
    },
  };
}
